// TIPSY TEACUP (§6.2, §12.5): a porcelain saucer floating on swirling tea. The saucer is a lathe
// (dish profile, raised lip, gold rim band, foot ring underneath) whose outer annulus is eight
// separate wedge pieces (one InstancedMesh) so Sudden Death can crack, shake and drop them; the
// whole saucer tilts visually with the replicated tilt (gameplay stays planar). The spinning cup
// turns with the hazard pose, its tea stays level and sloshes. A giant teapot pours into the tea,
// a giant spoon leans out of it and sugar cubes bob around.
import {
  CatmullRomCurve3, Color, CylinderGeometry, DynamicDrawUsage, Group, InstancedBufferAttribute, InstancedMesh, Matrix4, Mesh, Quaternion,
  SphereGeometry, TorusGeometry, TubeGeometry, Vector3,
} from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { RoundPhase } from '@shared/ids';
import { TUNING } from '@shared/maps/teacup';
import { hullGeometry, mergeKeep, seeded, sweep, type ProfilePoint } from '../kit/geometry';
import { outlineMaterial, surfaceMaterial } from '../kit/materials';
import { B, type Arena, type ArenaFrame, type ArenaKit, type ArenaLayout, type DebugHazard } from './types';

const R = TUNING.saucerRadius;
/** §6.2: the Sudden Death rim chips are the annulus r ∈ [4.5, 7.5] in 8 wedges of 45°. */
export const WEDGE_INNER = 4.5;
export const WEDGES = 8;
const WEDGE_SPAN = (Math.PI * 2) / WEDGES;
const CUP = TUNING.cup;
const PORCELAIN = '#FFF4E6';
const MINT = '#9DE0C8';
const GOLD = '#E8B64C';
const TEA = '#8A4B22';
const FOAM = '#F2D7B0';
/** Wedge fall: seconds until hidden. */
const FALL_S = 1.7;
/** §11.4: wedge warn shake 3 cm at 12 Hz. */
const SHAKE_M = 0.03;
const SHAKE_HZ = 12;

const glslColor = (c: string): string => `vec3(${new Color(c).toArray().map((v) => v.toFixed(4)).join(', ')})`;

// Saucer cross-sections (radius, height), counter-clockwise: bottom outward, rim up, top inward.
const WEDGE_RUNS: readonly (readonly ProfilePoint[])[] = [
  [[WEDGE_INNER, -0.44], [5.4, -0.4], [6.3, -0.31], [6.95, -0.2], [7.35, -0.08], [7.56, 0.05], [7.56, 0.12]],
  [[7.56, 0.12], [7.53, 0.16], [7.48, 0.15]],
  [[7.48, 0.15], [7.4, 0.1], [7.25, 0.035], [7.05, 0]],
  [[7.05, 0], [WEDGE_INNER, 0]],
  [[WEDGE_INNER, 0], [WEDGE_INNER, -0.44]],
];
const DISC_RUNS: readonly (readonly ProfilePoint[])[] = [
  // Underside with the foot ring.
  [[0, -0.6], [2.75, -0.6]],
  [[2.75, -0.6], [2.8, -0.86], [3.35, -0.86], [3.4, -0.56]],
  [[3.4, -0.56], [WEDGE_INNER, -0.44]],
  // The break face, then the top.
  [[WEDGE_INNER, -0.44], [WEDGE_INNER, 0]],
  [[WEDGE_INNER, 0], [0, 0]],
];

/** Porcelain, painted in object space (polar coordinates): well, hairlines, the mint scallop band, the gold rim. */
const PORCELAIN_ALBEDO = /* glsl */ `
  float r = length(envObj.xz);
  float ang = atan(envObj.z, envObj.x);
  float aa = fwidth(r) * 1.3 + 0.002;
  // A concave dish: the glaze brightens toward the middle, shades where the dish rises to the
  // band and catches the light again on the lip.
  vec3 porc = albedo * (0.95 + 0.07 * smoothstep(5.9, 2.4, r));
  porc *= 1.0 - 0.05 * smoothstep(5.0, 5.9, r) * (1.0 - smoothstep(5.9, 6.4, r));
  porc *= 1.0 + 0.05 * smoothstep(6.9, 7.3, r);
  vec3 mint = ${glslColor(MINT)};
  vec3 gold = ${glslColor(GOLD)};
  if (envNo.y > 0.55) {
    float well = smoothstep(${(CUP.radius - 0.02).toFixed(2)}, ${(CUP.radius + 0.06).toFixed(2)}, r) * (1.0 - smoothstep(1.62, 1.8, r));
    porc *= 1.0 - 0.08 * well;
    float hair = 1.0 - smoothstep(0.012, 0.012 + aa, abs(r - 2.45));
    porc = mix(porc, gold, hair * 0.85);
    // The break line of the rim chips: a dotted gold seam.
    float dots = step(0.42, fract(ang * 96.0 / 6.2831853));
    float seam = 1.0 - smoothstep(0.022, 0.022 + aa, abs(r - ${(WEDGE_INNER + 0.07).toFixed(2)}));
    porc = mix(porc, gold, seam * dots * 0.9);
    // Mint band with a scalloped inner edge and white dots (48 scallops: 6 per wedge).
    float f = fract(ang * 48.0 / 6.2831853);
    float bump = sqrt(max(0.0, 1.0 - (2.0 * f - 1.0) * (2.0 * f - 1.0)));
    float rIn = 6.02 - 0.24 * bump;
    float band = smoothstep(rIn - aa, rIn + aa, r) * (1.0 - smoothstep(6.66 - aa, 6.66 + aa, r));
    porc = mix(porc, mint, band);
    vec2 dp = vec2((f - 0.5) * r * 6.2831853 / 48.0, r - 6.36);
    float dotm = 1.0 - smoothstep(0.075, 0.075 + aa, length(dp));
    porc = mix(porc, albedo, dotm * band);
    float hair2 = 1.0 - smoothstep(0.016, 0.016 + aa, abs(r - 6.82));
    porc = mix(porc, gold, hair2);
  }
  // Gold rim band on the lip's crown.
  float rimBand = smoothstep(7.38, 7.44, r) * step(0.07, envObj.y);
  porc = mix(porc, gold * 1.05, rimBand);
  if (envNo.y < -0.3) {
    porc *= 0.9;
    // The maker's stamp on the underside: cobalt rings around a star, a gold line on the foot.
    vec3 cobalt = vec3(0.16, 0.26, 0.62);
    float stampR = 1.0 - smoothstep(0.03, 0.03 + aa, abs(r - 1.85));
    stampR = max(stampR, 1.0 - smoothstep(0.02, 0.02 + aa, abs(r - 1.55)));
    float sa = atan(envObj.z, envObj.x);
    float star = r - (0.62 + 0.38 * pow(abs(cos(sa * 2.5)), 6.0)) * 0.95;
    porc = mix(porc, cobalt, max(stampR, 1.0 - smoothstep(0.0, aa, star)) * 0.85);
    porc = mix(porc, gold, 1.0 - smoothstep(0.04, 0.04 + aa, abs(r - 3.08)));
  }
  albedo = porc;
`;

const CRACK_ALBEDO = /* glsl */ `
  // A fine groove where neighbouring wedges meet.
  float seamD = (${(Math.PI / 8).toFixed(5)} - abs(ang)) * r;
  albedo *= 1.0 - 0.14 * (1.0 - smoothstep(0.0, 0.035, seamD)) * step(0.5, envNo.y);
  if (vCrack > 0.001) {
    // Meandering, branching cracks: ridges of two noise fields.
    float r1 = abs(envFbm(envObj.xz * 0.75 + 11.0, 3) - 0.5);
    float r2 = abs(envFbm(envObj.xz * 1.9 + 3.0, 2) - 0.5);
    float lines = max(1.0 - smoothstep(0.006, 0.026, r1), (1.0 - smoothstep(0.005, 0.018, r2)) * 0.85);
    float reach = ${WEDGE_INNER.toFixed(2)} + vCrack * 3.6;
    float spread = 1.0 - smoothstep(reach - 0.5, reach, r);
    float seamCrack = 1.0 - smoothstep(0.0, 0.05, abs(r - ${(WEDGE_INNER + 0.06).toFixed(2)} - 0.05 * sin(ang * 70.0)));
    float k = clamp(lines * spread + seamCrack * min(1.0, vCrack * 4.0), 0.0, 1.0);
    albedo = mix(albedo, vec3(0.165, 0.10, 0.23), k);
    albedo = mix(albedo, vec3(1.0, 0.42, 0.38), 0.22 * vCrack * (0.5 + 0.5 * sin(uTime * 18.0)));
  }
`;

const CUP_ALBEDO = /* glsl */ `
  float rr = length(envObj.xz);
  float ang = atan(envObj.z, envObj.x);
  vec3 col = albedo;
  vec3 mint = ${glslColor(MINT)};
  vec3 gold = ${glslColor(GOLD)};
  bool outer = dot(envNo.xz, envObj.xz) > 0.0;
  if (rr > ${(CUP.radius + 0.05).toFixed(2)}) {
    col = gold;                                   // the handle
  } else if (outer) {
    // Mint polka dots on the outer wall; a gold band at the rim and the foot.
    vec2 g = vec2(ang * 7.0 / 3.14159265, envObj.y * 5.4);
    vec2 cell = floor(g);
    vec2 fc = fract(g) - 0.5;
    if (mod(cell.y, 2.0) > 0.5) { fc.x = fract(g.x + 0.5) - 0.5; }
    float d = length(fc * vec2(1.0, 1.05));
    float aa = fwidth(d) * 1.4 + 0.01;
    float inBand = step(0.16, envObj.y) * step(envObj.y, 0.66);
    col = mix(col, mint, (1.0 - smoothstep(0.24 - aa, 0.24 + aa, d)) * inBand);
    col = mix(col, gold, max(step(0.72, envObj.y), step(envObj.y, 0.075)));
  } else {
    col *= 0.93;
    col = mix(col, vec3(0.62, 0.38, 0.2), 0.35 * (1.0 - smoothstep(0.0, 0.03, abs(envObj.y - 0.66))));
    col = mix(col, gold, step(0.78, envObj.y));
  }
  albedo = col;
`;

/** Tea in the cup: a polar swirl turning with the cup, foam streaks, a cream heart. */
const TEA_ALBEDO = /* glsl */ `
  float rr = length(envObj.xz);
  float ang = atan(envObj.z, envObj.x) - uTime * 1.1 + 2.5 / (rr + 0.35);
  float swirl = envNoise(vec2(ang * 3.0, rr * 4.0 - uTime * 0.6));
  vec3 tea = ${glslColor(TEA)};
  vec3 foam = ${glslColor(FOAM)};
  vec3 col = tea * (0.85 + 0.35 * swirl);
  float streak = smoothstep(0.55, 0.8, envNoise(vec2(ang * 5.0, rr * 2.0)));
  col = mix(col, foam, streak * 0.55 * smoothstep(0.2, 0.9, rr));
  col = mix(col, foam * 1.02, smoothstep(0.92, 1.12, rr) * 0.85);
  // A latte heart in the middle, turning with the swirl.
  float ca = cos(uTime * 1.1); float sa = sin(uTime * 1.1);
  vec2 hp = mat2(ca, -sa, sa, ca) * envObj.xz * 3.2;
  hp.y = -hp.y + 0.25;
  float heart = pow(hp.x * hp.x + hp.y * hp.y - 1.0, 3.0) - hp.x * hp.x * hp.y * hp.y * hp.y;
  col = mix(col, foam * 1.05, (1.0 - smoothstep(-0.02, 0.02, heart)) * 0.9);
  albedo = col;
`;

/** Wedge `k` is centered on sim angle k·45° (§6.2); its bit in wedgeWarn / wedgeFallen is k. */
export function wedgeCenterAngle(k: number): number {
  return k * WEDGE_SPAN;
}

/** Wedge presentation: local timers, or WP2's pose timing (`crack`, `fallSec`) when the map provides it (−1 otherwise). */
interface WedgeAnim { warnAt: number; fallAt: number; fallen: boolean; hidden: boolean; seed: number; crack: number; fallSec: number }

/** Debug/test view of the teacup's animation state. */
export interface TeacupDebug {
  saucer: Group;
  wedges: InstancedMesh;
  wedgeState(k: number): 'solid' | 'warn' | 'falling' | 'gone';
}

export function buildTeacup(kit: ArenaKit): Arena & { teacup: TeacupDebug } {
  const root = new Group();
  root.name = 'arena:teacup';
  const owned: { dispose(): void }[] = [];
  const p = kit.palette;
  const env = kit.env;

  // ---- Tilting saucer ------------------------------------------------------------------------
  const saucer = new Group();
  saucer.name = 'saucer';
  root.add(saucer);
  const porcelain = surfaceMaterial(env, kit.ramp, { key: 'teacup-porcelain', color: PORCELAIN, rim: 0.32, gloss: 0.75, glossEdge: 0.95, albedo: PORCELAIN_ALBEDO });
  const segs = kit.seg(128, 48);
  const discGeo = sweep(DISC_RUNS, segs);
  const disc = new Mesh(discGeo, porcelain);
  disc.name = 'saucer-disc';
  disc.castShadow = true;
  disc.receiveShadow = true;
  saucer.add(disc);
  owned.push(discGeo, porcelain);

  // The eight rim wedges.
  const wedgeGeo = sweep(WEDGE_RUNS, Math.max(6, Math.round(segs / WEDGES)), -WEDGE_SPAN / 2, WEDGE_SPAN / 2, true);
  const crack = new Float32Array(WEDGES);
  const crackAttr = new InstancedBufferAttribute(crack, 1);
  crackAttr.setUsage(DynamicDrawUsage);
  wedgeGeo.setAttribute('aCrack', crackAttr);
  const wedgeMat = surfaceMaterial(env, kit.ramp, {
    key: 'teacup-wedge', color: PORCELAIN, rim: 0.32, gloss: 0.75, glossEdge: 0.95,
    albedo: PORCELAIN_ALBEDO + CRACK_ALBEDO, pars: 'varying float vCrack;\n', vertexPars: 'attribute float aCrack;\n', vertexEnd: 'vCrack = aCrack;',
  });
  const wedges = new InstancedMesh(wedgeGeo, wedgeMat, WEDGES);
  wedges.name = 'saucer-wedges';
  wedges.castShadow = true;
  wedges.receiveShadow = true;
  wedges.instanceMatrix.setUsage(DynamicDrawUsage);
  wedges.frustumCulled = false;
  wedges.userData.hazard = true;
  saucer.add(wedges);
  owned.push(wedgeGeo, wedgeMat);
  // The outline hull leaves out the radial end caps: neighbouring wedges meet in a painted groove,
  // not an ink line that would read as a crack before any wedge is warned.
  const wedgeHullSrc = sweep(WEDGE_RUNS, Math.max(6, Math.round(segs / WEDGES)), -WEDGE_SPAN / 2, WEDGE_SPAN / 2, false);
  const wedgeHullGeo = hullGeometry(wedgeHullSrc);
  wedgeHullSrc.dispose();
  const wedgeHullMat = outlineMaterial(0.05, kit.outlineScale);
  const wedgeHull = new InstancedMesh(wedgeHullGeo, wedgeHullMat, WEDGES);
  wedgeHull.instanceMatrix = wedges.instanceMatrix;
  wedgeHull.frustumCulled = false;
  wedgeHull.name = 'saucer-wedges-outline';
  saucer.add(wedgeHull);
  owned.push(wedgeHullGeo, wedgeHullMat);
  const breakFace = sweep([[[WEDGE_INNER, -0.44], [WEDGE_INNER, 0]]], segs);
  const discHullGeo = hullGeometry(breakFace);
  breakFace.dispose();
  const discHull = new Mesh(discHullGeo, kit.mats.hullBold);
  discHull.name = 'saucer-disc-outline';
  saucer.add(discHull);
  owned.push(discHullGeo);

  // ---- Spinning cup --------------------------------------------------------------------------
  const cup = new Group();
  cup.name = 'cup';
  saucer.add(cup);
  const cupMat = surfaceMaterial(env, kit.ramp, { key: 'teacup-cup', color: PORCELAIN, rim: 0.35, gloss: 0.8, glossEdge: 0.95, albedo: CUP_ALBEDO });
  owned.push(cupMat);
  const cupProfile: (readonly ProfilePoint[])[] = [
    [[0, 0.02], [0.62, 0.02]],
    [[0.62, 0.02], [0.7, 0.0], [0.74, 0.06], [0.72, 0.1]],
    [[0.72, 0.1], [0.82, 0.16], [1.02, 0.28], [1.18, 0.44], [1.27, 0.6], [CUP.radius, CUP.top]],
    [[CUP.radius, CUP.top], [1.29, CUP.top + 0.035], [1.25, CUP.top + 0.03], [1.23, CUP.top]],
    [[1.23, CUP.top], [1.2, 0.64], [1.1, 0.46], [0.92, 0.32], [0.6, 0.22], [0, 0.2]],
  ];
  const cupGeo = sweep(cupProfile, kit.seg(56, 24));
  const handle = new TorusGeometry(0.27, 0.075, kit.seg(10, 6), kit.seg(24, 12), Math.PI * 1.35);
  handle.rotateZ(-Math.PI * 0.675);
  handle.translate(CUP.radius + 0.08, 0.44, 0);
  const cupAll = mergeKeep([cupGeo, handle], ['position', 'normal', 'uv']);
  const cupMesh = new Mesh(cupAll, cupMat);
  cupMesh.castShadow = true;
  cupMesh.receiveShadow = true;
  cupMesh.name = 'cup-body';
  cupMesh.userData.hazard = true;
  cup.add(cupMesh);
  const cupHullGeo = hullGeometry(cupAll);
  const cupHull = new Mesh(cupHullGeo, kit.mats.hull);
  cup.add(cupHull);
  owned.push(cupAll, cupHullGeo);
  // Tea surface: stays nearly level while the saucer tilts, and sloshes.
  const teaMat = surfaceMaterial(env, kit.ramp, { key: 'teacup-tea', color: TEA, rim: 0.2, gloss: 0.9, glossEdge: 0.9, albedo: TEA_ALBEDO });
  const teaGeo = new CylinderGeometry(1.17, 1.17, 0.02, kit.seg(40, 18));
  const tea = new Mesh(teaGeo, teaMat);
  tea.position.y = 0.62;
  tea.name = 'cup-tea';
  saucer.add(tea);
  owned.push(teaGeo, teaMat);

  // ---- Background: teapot pouring, giant spoon, sugar cubes, lemon slices ----------------------
  const pour = teapotAndProps(kit, root, owned);

  // ---- Animation state -------------------------------------------------------------------------
  const anim: WedgeAnim[] = Array.from({ length: WEDGES }, (_, k) => ({ warnAt: -1, fallAt: -1, fallen: false, hidden: false, seed: k * 7.31 + 1, crack: -1, fallSec: -1 }));
  let first = true;
  const tiltQ = new Quaternion();
  const up = new Vector3(0, 1, 0);
  const n = new Vector3();
  const m = new Matrix4();
  const tmp = new Matrix4();
  const zero = new Vector3(0, 0, 0);
  const pivotIn = new Matrix4().makeTranslation(-WEDGE_INNER, 0, 0);
  const pivotOut = new Matrix4();
  const rot = new Matrix4();
  const chipV = new Vector3();
  let slope = 0;
  let dirX = 1;
  let dirY = 0;
  // Slosh: a damped spring on the tea surface's tilt (relative to world level).
  const slosh = { x: 0, y: 0, vx: 0, vy: 0, lastTx: 0, lastTy: 0 };

  function writeWedges(time: number): void {
    for (let k = 0; k < WEDGES; k++) {
      const a = anim[k];
      m.makeRotationY(-wedgeCenterAngle(k));
      if (a.hidden) {
        m.scale(zero);
      } else if (a.fallen) {
        const t = a.fallSec >= 0 ? a.fallSec : Math.max(0, time - a.fallAt);
        const tip = Math.min(1.35, 2.6 * t * t + 0.6 * t);
        const drop = t < 0.18 ? 0 : 7 * (t - 0.18) * (t - 0.18);
        pivotOut.makeTranslation(WEDGE_INNER + 1.1 * t, -drop, 0);
        rot.makeRotationZ(-tip);
        m.multiply(pivotOut).multiply(rot).multiply(pivotIn);
        if (t > FALL_S) a.hidden = true;
      } else if (a.warnAt >= 0) {
        const t = time - a.warnAt;
        const s = SHAKE_M * Math.min(1, t * 3);
        const ph = t * SHAKE_HZ * Math.PI * 2;
        m.multiply(tmp.makeTranslation(Math.sin(ph + a.seed) * s, Math.abs(Math.sin(ph * 0.5 + a.seed)) * s * 0.6, Math.cos(ph * 1.3 + a.seed) * s));
      }
      wedges.setMatrixAt(k, m);
      crack[k] = a.fallen ? 1 : a.crack >= 0 ? a.crack : a.warnAt >= 0 ? Math.min(1, (time - a.warnAt) / 1.4) : 0;
    }
    wedges.instanceMatrix.needsUpdate = true;
    crackAttr.needsUpdate = true;
  }

  /** §11.4: 20 porcelain chips along the break line of wedge k. */
  function chips(k: number): void {
    const a0 = wedgeCenterAngle(k);
    for (let i = 0; i < 5; i++) {
      const a = a0 + (i / 4 - 0.5) * WEDGE_SPAN * 0.9;
      chipV.set(Math.cos(a) * (WEDGE_INNER + 0.1), 0.05, Math.sin(a) * (WEDGE_INNER + 0.1)).applyQuaternion(tiltQ);
      kit.particles.burst({ x: chipV.x, y: chipV.y, z: chipV.z, count: 4, color: [PORCELAIN, PORCELAIN, MINT, GOLD], speed: 2.2, up: 4.5, size: 0.09, life: 1.4, spread: 0.25, gravity: 16 });
    }
  }

  writeWedges(0);

  const layout: ArenaLayout = {
    sky: { top: p.skyTop, bottom: p.skyBottom, cloud: '#FFF6EE', clouds: 0.7, stars: 0, sunGlow: 0.9 },
    voidColors: { a: TEA, b: FOAM, c: '#FFE3C8' },
    voidEdge: R,
    voidPour: { x: pour.x, y: pour.z, r: 1.1 },
    splash: { ring: FOAM, edge: '#FFFFFF' },
    audience: { cx: 0, cy: 0, rx: R + 6.2, ry: R + 5.6, y: -0.8, plank: '#F6E7D2', trim: MINT, fur: ['#FFB3C7', '#9DE0C8', '#FFE3C8', '#C8B6FF', '#FFFFFF', '#F2C28B', '#7FD0FF'] },
    confetti: { colors: [MINT, GOLD, '#FFB3C7', '#C8B6FF', '#FF8FB1'], r0: R + 10, r1: R + 34, y0: -2, y1: 18 },
    shadowY: [-1.0, 3.0],
  };

  return {
    root, layout,
    teacup: {
      saucer, wedges,
      wedgeState(k) {
        const a = anim[k];
        return a.hidden ? 'gone' : a.fallen ? 'falling' : a.warnAt >= 0 ? 'warn' : 'solid';
      },
    },
    update(f: ArenaFrame) {
      const v = f.view;
      const map = v.map;
      // Tilt (§6.2): the visual saucer turns about the origin by the replicated tilt vector.
      const tx = map.tiltX;
      const ty = map.tiltY;
      const mag = Math.hypot(tx, ty);
      slope = Math.tan(Math.min(mag, 0.6));
      if (mag > 1e-6) {
        dirX = tx / mag;
        dirY = ty / mag;
      }
      n.set(dirX * slope, 1, dirY * slope).normalize();
      tiltQ.setFromUnitVectors(up, n);
      saucer.quaternion.copy(tiltQ);
      // Cup spin: θc from the hazard pose (sim angle → three.js yaw).
      cup.rotation.y = -v.hazard.cupAngle;
      // Tea: counter-tilt 60% so it stays near level, plus a slosh spring driven by tilt changes.
      const dt = Math.max(1e-4, f.dt);
      const ax = f.dt > 0 ? (tx - slosh.lastTx) / dt : 0;
      const ay = f.dt > 0 ? (ty - slosh.lastTy) / dt : 0;
      slosh.lastTx = tx;
      slosh.lastTy = ty;
      slosh.vx += (-slosh.x * 60 - slosh.vx * 4 - ax * 0.6) * f.dt;
      slosh.vy += (-slosh.y * 60 - slosh.vy * 4 - ay * 0.6) * f.dt;
      slosh.x += slosh.vx * f.dt;
      slosh.y += slosh.vy * f.dt;
      const lx = -tx * 0.6 + slosh.x + Math.sin(f.time * 2.1) * 0.012;
      const ly = -ty * 0.6 + slosh.y + Math.cos(f.time * 1.7) * 0.012;
      const lm = Math.hypot(lx, ly);
      const ls = Math.tan(Math.min(0.3, lm));
      n.set(lm > 1e-6 ? (lx / lm) * ls : 0, 1, lm > 1e-6 ? (ly / lm) * ls : 0).normalize();
      tea.quaternion.setFromUnitVectors(up, n);
      // Wedges: the replicated bits say which wedges are warned or gone (§6.2); WP2's pose adds the
      // exact crack progress and seconds since the fall, so replays and late joins animate in step.
      const warn = map.wedgeWarn;
      const fallen = map.wedgeFallen;
      const inRound = v.clock.phase !== RoundPhase.INTRO;
      const poseCrack = v.hazard.wedgeCrack;
      const poseFall = v.hazard.wedgeFallSec;
      for (let k = 0; k < WEDGES; k++) {
        const bit = 1 << k;
        const a = anim[k];
        a.crack = poseCrack && poseCrack[k] > 0 ? poseCrack[k] : -1;
        a.fallSec = poseFall && poseFall[k] >= 0 ? poseFall[k] : -1;
        const isFallen = (fallen & bit) !== 0 || a.fallSec >= 0;
        if (isFallen) {
          if (!a.fallen) {
            a.fallen = true;
            a.fallAt = f.time;
            a.warnAt = -1;
            const late = a.fallSec > FALL_S;
            if (first || !inRound || late) a.hidden = true;
            else chips(k);
          } else if (a.hidden && a.fallSec >= 0 && a.fallSec < FALL_S) {
            a.hidden = false; // a replay rewound into the fall
          }
        } else {
          if (a.fallen) {
            a.fallen = false;
            a.hidden = false;
          }
          if ((warn & bit) !== 0 || a.crack > 0) {
            if (a.warnAt < 0) a.warnAt = f.time;
          } else a.warnAt = -1;
        }
      }
      first = false;
      writeWedges(f.time);
    },
    onEvent() {
      // Wedges animate from the replicated bits; the WEDGE event exists for stats and audio.
    },
    surfaceOffset(x, y) {
      // Height of the tilted plane at sim (x, y): the saucer normal is (dir·slope, 1).
      return -(dirX * x + dirY * y) * slope;
    },
    debugHazards() {
      const c = new Vector3(0, CUP.top, 0).applyQuaternion(tiltQ);
      const out: DebugHazard[] = [{ kind: 'cup', index: 0, a: c, b: c.clone(), radius: CUP.radius }];
      return out;
    },
    dispose() {
      for (const o of owned) o.dispose();
    },
  };
}

/** Background diorama props (static, merged into the bg bucket; no shadows) and the tea stream. */
function teapotAndProps(kit: ArenaKit, root: Group, owned: { dispose(): void }[]): Vector3 {
  const bg = kit.batch;
  const rnd = seeded(kit.seed * 13 + 3);
  const seg = (count: number): number => kit.seg(count, 8);
  // Giant teapot, half-sunk in the tea at the back left, spout toward the saucer.
  const tp = new Vector3(-14, -2.4, -13);
  const bodyProfile: readonly ProfilePoint[] = [[0, -3], [3.6, -2.4], [4.6, -0.4], [4.4, 1.4], [3.4, 2.7], [2.2, 3.2], [0, 3.25]];
  const at = new Matrix4().makeTranslation(tp.x, tp.y, tp.z);
  bg.add(B.bg, sweep([bodyProfile], seg(40)), at, { color: PORCELAIN, ao: [tp.y - 2, tp.y + 1.5, 0.82] });
  const band = new TorusGeometry(4.5, 0.22, 6, seg(48));
  band.rotateX(Math.PI / 2);
  bg.add(B.bg, band, new Matrix4().makeTranslation(tp.x, tp.y + 0.6, tp.z), { color: MINT });
  const band2 = new TorusGeometry(3.1, 0.12, 6, seg(40));
  band2.rotateX(Math.PI / 2);
  bg.add(B.bg, band2, new Matrix4().makeTranslation(tp.x, tp.y + 2.8, tp.z), { color: GOLD });
  bg.add(B.bg, sweep([[[0, 3.2], [2.3, 3.2], [2.0, 3.7], [0.9, 4.15], [0, 4.2]]], seg(32)), at, { color: PORCELAIN });
  bg.add(B.bg, new SphereGeometry(0.55, seg(14), seg(10)), new Matrix4().makeTranslation(tp.x, tp.y + 4.55, tp.z), { color: GOLD });
  // Spout: a tapered tube along a curve from the body toward the saucer.
  const spoutDir = new Vector3(9, 0, 7).normalize();
  const s0 = tp.clone().addScaledVector(spoutDir, 3.8).add(new Vector3(0, -0.2, 0));
  const spout = new CatmullRomCurve3([s0, s0.clone().addScaledVector(spoutDir, 1.8).add(new Vector3(0, 1.3, 0)),
    s0.clone().addScaledVector(spoutDir, 3.4).add(new Vector3(0, 3.0, 0))]);
  const radial = seg(14);
  const spoutGeo = new TubeGeometry(spout, 16, 0.75, radial, false);
  const sp = spoutGeo.getAttribute('position');
  const pts = spout.getSpacedPoints(16);
  for (let i = 0; i < sp.count; i++) {
    const ring = Math.min(16, Math.floor(i / (radial + 1)));
    const c = pts[ring];
    const k = 1 - 0.45 * (ring / 16);
    sp.setXYZ(i, c.x + (sp.getX(i) - c.x) * k, c.y + (sp.getY(i) - c.y) * k, c.z + (sp.getZ(i) - c.z) * k);
  }
  spoutGeo.computeVertexNormals();
  bg.add(B.bg, spoutGeo, null, { color: PORCELAIN });
  const tip = pts[16];
  const lipRing = new TorusGeometry(0.42, 0.1, 6, seg(18));
  lipRing.lookAt(spout.getTangent(1));
  bg.add(B.bg, lipRing, new Matrix4().makeTranslation(tip.x, tip.y, tip.z), { color: GOLD });
  const handle = new TorusGeometry(2.0, 0.42, seg(10), seg(24), Math.PI * 1.25);
  handle.rotateZ(-Math.PI * 0.625);
  const hm = new Matrix4().makeRotationY(Math.atan2(spoutDir.z, -spoutDir.x)).setPosition(tp.x - spoutDir.x * 4.6, tp.y + 0.9, tp.z - spoutDir.z * 4.6);
  bg.add(B.bg, handle, hm, { color: PORCELAIN });
  if (kit.rich) bg.add(B.bgHull, sweep([bodyProfile], 24), at);

  // The tea stream from the spout into the void (one animated mesh).
  const t1 = spout.getTangent(1);
  const pourEnd = new Vector3(tip.x + spoutDir.x * 1.6, -3.05, tip.z + spoutDir.z * 1.6);
  const stream = new CatmullRomCurve3([tip.clone().addScaledVector(t1, 0.25), tip.clone().add(new Vector3(spoutDir.x * 1.1, -0.9, spoutDir.z * 1.1)), pourEnd]);
  const streamGeo = new TubeGeometry(stream, 24, 0.3, 10, false);
  const streamMat = surfaceMaterial(kit.env, kit.ramp, {
    key: 'teacup-stream', color: TEA, rim: 0.45, gloss: 0.9, glossEdge: 0.88,
    albedo: /* glsl */ `
      float flow = envNoise(vec2(envUv.y * 9.0, envUv.x * 5.0 - uTime * 4.5));
      albedo = mix(albedo * 1.05, vec3(0.86, 0.6, 0.36), smoothstep(0.55, 0.9, flow) * 0.55);`,
  });
  const streamMesh = new Mesh(streamGeo, streamMat);
  streamMesh.name = 'tea-stream';
  root.add(streamMesh);
  owned.push(streamGeo, streamMat);

  // Giant chrome teaspoon at the back right: its bowl dipped in the tea, the handle rising out of it.
  const spoon = new Matrix4().makeRotationY(-0.95).multiply(new Matrix4().makeRotationX(0.4)).multiply(new Matrix4().makeScale(0.8, 0.8, 0.8)).setPosition(9.6, -2.95, -9.8);
  const bowlProfile: readonly ProfilePoint[] = [[0, -0.55], [0.9, -0.48], [1.5, -0.25], [1.75, 0.0], [1.68, 0.06], [1.4, -0.06], [0.8, -0.14], [0, -0.16]];
  const steel = '#E4EAF1';
  bg.add(B.chrome, sweep([bowlProfile], seg(28)), spoon.clone().multiply(new Matrix4().makeScale(1, 1, 1.45)), { color: steel });
  const neck = new CylinderGeometry(0.22, 0.34, 2.4, seg(10));
  neck.rotateX(Math.PI / 2);
  bg.add(B.chrome, neck, spoon.clone().multiply(new Matrix4().makeTranslation(0, 0.02, -3.4)).multiply(new Matrix4().makeScale(1.4, 0.5, 1)), { color: steel });
  const stem = new CylinderGeometry(0.62, 0.3, 8, seg(12));
  stem.rotateX(Math.PI / 2);
  bg.add(B.chrome, stem, spoon.clone().multiply(new Matrix4().makeTranslation(0, 0.02, -8.4)).multiply(new Matrix4().makeScale(1.3, 0.3, 1)), { color: steel });
  bg.add(B.chrome, new SphereGeometry(1.0, seg(16), seg(10)), spoon.clone().multiply(new Matrix4().makeTranslation(0, 0.02, -12.4)).multiply(new Matrix4().makeScale(1.05, 0.32, 1.25)), { color: steel });
  bg.add(B.chrome, new SphereGeometry(0.34, seg(10), seg(8)), spoon.clone().multiply(new Matrix4().makeTranslation(0, 0.26, -12.4)).multiply(new Matrix4().makeScale(1, 0.55, 1)), { color: GOLD });
  if (kit.rich) {
    bg.add(B.bgHull, sweep([bowlProfile], 16), spoon.clone().multiply(new Matrix4().makeScale(1, 1, 1.45)));
    const hs = new CylinderGeometry(0.62, 0.3, 8, 8);
    hs.rotateX(Math.PI / 2);
    bg.add(B.bgHull, hs, spoon.clone().multiply(new Matrix4().makeTranslation(0, 0.02, -8.4)).multiply(new Matrix4().makeScale(1.3, 0.3, 1)));
  }

  // Floating sugar cubes and lemon slices around the saucer.
  const cubeSpots: [number, number][] = [[-11.5, -2], [10.5, 3.5], [-9.5, 6.5], [4.5, -12.5], [-4, -12.5], [12, -2.5]];
  for (const [x, z] of cubeSpots) {
    const s = 0.9 + rnd() * 0.5;
    const mm = new Matrix4().makeRotationY(rnd() * Math.PI).multiply(new Matrix4().makeRotationX((rnd() - 0.5) * 0.5)).setPosition(x, -3 + s * 0.25, z);
    bg.add(B.bg, new RoundedBoxGeometry(s, s, s, 2, s * 0.14), mm, { color: '#F4EFE8', ao: [-3.05, -2.6, 0.78] });
    if (kit.rich) bg.add(B.bgHull, new RoundedBoxGeometry(s, s, s, 1, s * 0.14), mm);
  }
  for (const [x, z, rr] of [[9.5, 7.5, 1.3], [-12.5, 3.5, 1.1]] as const) {
    bg.add(B.bg, new CylinderGeometry(rr, rr, 0.24, seg(28)), new Matrix4().makeTranslation(x, -2.95, z), { color: '#FFD54A' });
    bg.add(B.bg, new CylinderGeometry(rr * 0.88, rr * 0.88, 0.26, seg(28)), new Matrix4().makeTranslation(x, -2.94, z), { color: '#FFF3A6' });
    for (let k = 0; k < 4; k++) {
      const spoke = new CylinderGeometry(0.035, 0.035, rr * 1.7, 3);
      spoke.rotateZ(Math.PI / 2);
      bg.add(B.bg, spoke, new Matrix4().makeRotationY((k * Math.PI) / 4).setPosition(x, -2.8, z), { color: '#FFE070' });
    }
  }
  return pourEnd;
}
